import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Wallet, Download, Loader2, RefreshCw, Eye, Settings as SettingsIcon, Save } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface PrepaymentSummary {
  id: number;
  title: string;
  supplierName: string | null;
  startDate: string | null;
  endDate: string | null;
  prepaymentAmount: number;
  monthsTotal: number;
  monthsReleased: number;
  monthsRemaining: number;
  releasedAmount: number;
  pendingAmount: number;
  remainingAmount: number;
}

interface JournalPreview {
  periodMonth: string;
  rowCount: number;
  totalAmount: number;
  contractIds: number[];
  missingNominalContractIds: number[];
  csvPreview: string;
}

// Defaults to the current calendar month — operators almost always run
// the journal at month-end for the month that just closed, so this
// avoids "wrong period" mistakes more often than starting on the
// previous month would.
function currentPeriodMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function PrepaymentsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";

  const [periodMonth, setPeriodMonth] = useState(currentPeriodMonth());
  const [defaultsDr, setDefaultsDr] = useState<string>("");
  const [defaultsCr, setDefaultsCr] = useState<string>("");
  const [defaultsDirty, setDefaultsDirty] = useState(false);

  const { data: prepayments = [], isLoading } = useQuery<PrepaymentSummary[]>({
    queryKey: ["prepayments"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/prepayments`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load prepayments");
      return res.json();
    },
  });

  const { data: settings } = useQuery<{ defaultNominalDr: string | null; defaultNominalCr: string | null }>({
    queryKey: ["prepayment-settings"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/prepayment-settings`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load settings");
      const data = await res.json();
      // Seed local form state on first successful fetch only — re-fetches
      // shouldn't blow away in-progress edits.
      if (!defaultsDirty) {
        setDefaultsDr(data.defaultNominalDr ?? "");
        setDefaultsCr(data.defaultNominalCr ?? "");
      }
      return data;
    },
  });

  const { data: preview } = useQuery<JournalPreview>({
    queryKey: ["prepayment-journal-preview", periodMonth],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/prepayments/journal/preview?periodMonth=${periodMonth}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Preview failed");
      return res.json();
    },
    enabled: /^\d{4}-\d{2}$/.test(periodMonth),
  });

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/prepayment-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ defaultNominalDr: defaultsDr.trim() || null, defaultNominalCr: defaultsCr.trim() || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Save failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setDefaultsDirty(false);
      qc.invalidateQueries({ queryKey: ["prepayment-settings"] });
      qc.invalidateQueries({ queryKey: ["prepayment-journal-preview"] });
      toast({ title: "Defaults saved" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const regenerateMutation = useMutation({
    mutationFn: async (contractId: number) => {
      const res = await fetch(`${BASE}/api/prepayments/${contractId}/regenerate`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Regenerate failed");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["prepayments"] });
      qc.invalidateQueries({ queryKey: ["prepayment-journal-preview"] });
      // Surface accounting drift loudly. Either flag means the operator
      // edited the contract into a state the schedule can't reconcile,
      // and a manual correction journal is needed.
      const drift: string[] = [];
      if (data?.overReleasedAmount != null) {
        drift.push(`£${data.overReleasedAmount.toFixed(2)} already released exceeds new total`);
      }
      if (data?.unscheduledRemainder != null) {
        drift.push(`£${data.unscheduledRemainder.toFixed(2)} balance left with no months to release into`);
      }
      if (drift.length > 0) {
        toast({
          title: "Schedule rebuilt — accounting exception",
          description: drift.join("; ") + ". Raise a manual correction journal.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Schedule rebuilt" });
      }
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  // Journal CSV download. We POST then trigger a browser download from
  // the response blob so the request is authenticated via the session
  // cookie — a plain <a download> would bypass any error feedback.
  const postJournalMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/prepayments/journal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ periodMonth }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Journal post failed" }));
        throw new Error(err.error || "Journal post failed");
      }
      const batchId = res.headers.get("X-Journal-Batch-Id") || "";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prepayment-journal-${periodMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { batchId };
    },
    onSuccess: ({ batchId }) => {
      qc.invalidateQueries({ queryKey: ["prepayments"] });
      qc.invalidateQueries({ queryKey: ["prepayment-journal-preview", periodMonth] });
      toast({ title: `Journal posted (${batchId})`, description: "Releases marked as posted; CSV downloaded." });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
              <Wallet className="w-6 h-6 text-duffells-orange" />
              Prepayments
            </h1>
            <p className="text-sm text-duffells-grey/60 mt-1">
              Automatic monthly release schedule for upfront-paid contracts.
              Generate the Merlin journal CSV at month-end.
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-duffells-grey flex items-center gap-2">
                <SettingsIcon className="w-4 h-4" />
                Default nominal codes
              </h3>
              <p className="text-xs text-duffells-grey/60 mt-1">
                Used for every prepayment journal unless a contract has its own override.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>DR (P&amp;L expense) code</Label>
                <Input
                  value={defaultsDr}
                  onChange={(e) => { setDefaultsDr(e.target.value); setDefaultsDirty(true); }}
                  placeholder="e.g. 5000"
                />
              </div>
              <div>
                <Label>CR (Prepayments balance sheet) code</Label>
                <Input
                  value={defaultsCr}
                  onChange={(e) => { setDefaultsCr(e.target.value); setDefaultsDirty(true); }}
                  placeholder="e.g. 1240"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => saveSettingsMutation.mutate()}
                disabled={!defaultsDirty || saveSettingsMutation.isPending}
              >
                {saveSettingsMutation.isPending
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Save className="w-4 h-4 mr-2" />}
                Save defaults
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-semibold text-duffells-grey">Generate journal</h3>
              <p className="text-xs text-duffells-grey/60 mt-1">
                Posts every pending release for the selected month, downloads
                the Merlin-import CSV, and marks those rows as posted.
              </p>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label>Accounting period</Label>
                <Input
                  type="month"
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                  className="w-44"
                />
              </div>
              <Button
                onClick={() => postJournalMutation.mutate()}
                disabled={
                  postJournalMutation.isPending
                  || !preview
                  || preview.rowCount === 0
                  || preview.missingNominalContractIds.length > 0
                }
              >
                {postJournalMutation.isPending
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Generate &amp; download CSV
              </Button>
            </div>
          </div>

          {preview && (
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-duffells-grey">
                  <strong>{preview.rowCount}</strong> pending release{preview.rowCount === 1 ? "" : "s"} for {periodMonth}
                  {preview.rowCount > 0 && (
                    <span> · total <strong>£{preview.totalAmount.toFixed(2)}</strong></span>
                  )}
                </div>
                {preview.missingNominalContractIds.length > 0 && (
                  <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">
                    {preview.missingNominalContractIds.length} contract(s) missing nominal codes
                  </span>
                )}
              </div>
              {preview.rowCount > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-duffells-grey/70 inline-flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    CSV preview
                  </summary>
                  <pre className="mt-2 p-3 bg-white border border-slate-200 rounded text-[11px] overflow-x-auto">
                    {preview.csvPreview}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-duffells-grey">Prepayment contracts</h3>
            <p className="text-xs text-duffells-grey/60 mt-1">
              Every contract ticked as a prepayment. Click through to edit the
              schedule or change accounting codes.
            </p>
          </div>
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-duffells-grey/60" />
            </div>
          ) : prepayments.length === 0 ? (
            <div className="p-8 text-center text-sm text-duffells-grey/60">
              No prepayment contracts yet. Open a contract and tick "This contract is a prepayment".
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-duffells-grey/60">
                  <tr>
                    <th className="px-4 py-2">Contract</th>
                    <th className="px-4 py-2">Period</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Released</th>
                    <th className="px-4 py-2 text-right">Remaining</th>
                    <th className="px-4 py-2">Progress</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {prepayments.map((p) => {
                    const pct = p.monthsTotal > 0 ? (p.monthsReleased / p.monthsTotal) * 100 : 0;
                    return (
                      <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                        <td className="px-4 py-3">
                          <Link href={`/contracts/${p.id}`} className="font-medium text-duffells-grey hover:text-primary">
                            {p.title}
                          </Link>
                          {p.supplierName && (
                            <div className="text-xs text-duffells-grey/60">{p.supplierName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-duffells-grey/80 text-xs">
                          {p.startDate || "—"} → {p.endDate || "—"}
                        </td>
                        <td className="px-4 py-3 text-right">£{p.prepaymentAmount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-emerald-700">£{p.releasedAmount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-semibold">£{p.remainingAmount.toFixed(2)}</td>
                        <td className="px-4 py-3 w-48">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-duffells-orange" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-duffells-grey/60 w-12 text-right">
                              {p.monthsReleased}/{p.monthsTotal}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => regenerateMutation.mutate(p.id)}
                            disabled={regenerateMutation.isPending}
                            className="text-xs text-duffells-grey/60 hover:text-primary inline-flex items-center gap-1"
                            title="Rebuild the unposted portion of this contract's schedule"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Re-sync
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
